<script setup>
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, ref } from "vue";
import moment from "moment";
import orderApi from "@/api/order";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const router = useRouter();
const $loading = useLoading({});

const orderList = computed(() => store.getters['order/orderList']);
const deviceList = computed(() => store.getters['locker/lockerList']);
const organizationList = computed(() => store.getters['organization/organizationList']);
const currentUserData = computed(() => store.getters['user/currentUserData']);
const orders = computed(() => orderList.value?.results || []);
const isAdmin = computed(() => currentUserData.value.role === 'administrator');

const page = ref(1);
const searchOrderId = ref('');
const status = ref('');
const device = ref('');
const organization = ref('');

const STATUS_META = {
  created: { pill: 'created', label: 'Created' },
  active: { pill: 'active', label: 'Active' },
  finished: { pill: 'finished', label: 'Finished' },
  expired: { pill: 'expired', label: 'Expired' },
};

function getFilteredOrderList(showLoader = true) {
  const loader = showLoader ? $loading.show({}) : null;
  let filter = `?page=${page.value}`;
  if (status.value) filter += `&status=${status.value}`;
  if (searchOrderId.value) filter += `&id=${searchOrderId.value}`;
  if (device.value) filter += `&device=${device.value}`;
  if (organization.value) filter += `&organization=${organization.value}`;
  return store.dispatch('order/getOrderList', filter)
    .finally(() => loader && loader.hide());
}

let searchDebounce = null;
function onSearchInput() {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    page.value = 1;
    getFilteredOrderList(false);
  }, 300);
}

function applyFilters() {
  page.value = 1;
  getFilteredOrderList();
}

// counters by status: there's no dedicated order-statistics endpoint,
// so we take the count from four requests with page_size=1
const statusCounts = ref({ created: 0, active: 0, finished: 0, expired: 0 });
const totalOrders = ref(0);

function loadCounts() {
  orderApi.getOrderList('?page_size=1')
    .then((r) => { totalOrders.value = r.data.count ?? 0; })
    .catch(() => {});
  Object.keys(statusCounts.value).forEach((st) => {
    orderApi.getOrderList(`?status=${st}&page_size=1`)
      .then((r) => { statusCounts.value[st] = r.data.count ?? 0; })
      .catch(() => {});
  });
}

onMounted(() => {
  getFilteredOrderList();
  loadCounts();
  store.dispatch('locker/getLockerList', '?page_size=1000');
  store.dispatch('organization/getOrganizationList', '?page_size=1000');
});

const orderMetrics = computed(() => [
  { label: 'Created', value: statusCounts.value.created, icon: 'plus-circle-outline', color: 'var(--lb-blue)' },
  { label: 'Active', value: statusCounts.value.active, icon: 'lock-clock', color: 'var(--lb-success)' },
  { label: 'Finished', value: statusCounts.value.finished, icon: 'check-circle-outline', color: 'var(--lb-slate)' },
  { label: 'Expired', value: statusCounts.value.expired, icon: 'clock-alert-outline', color: 'var(--lb-red)' },
]);

// TODO(avg order time / last week orders): no backend endpoint for this
// statistic yet — demo data for now, swap for real data once the API exists.
const avgOrderTime = { value: '6.8', unit: 'h', foot: 'Avg rental duration · 30 days' };
const weekDemo = [
  { day: 'Mon', v: 18 }, { day: 'Tue', v: 24 }, { day: 'Wed', v: 21 },
  { day: 'Thu', v: 30 }, { day: 'Fri', v: 27 }, { day: 'Sat', v: 15 }, { day: 'Sun', v: 12 },
];
const weekMax = Math.max(...weekDemo.map((d) => d.v));
const weekTotal = weekDemo.reduce((s, d) => s + d.v, 0);

const fmt = (ts) => (ts ? moment(ts).format('MMM D, HH:mm') : '—');

function goToOrder(o) {
  router.push({ name: 'order-detail', params: { id: o.id } });
}

const cols = 'minmax(110px,0.8fr) minmax(130px,1fr) 70px 110px 130px 130px 130px 130px minmax(140px,1fr)';
const columns = ['Order ID', 'Device', 'Cell', 'Status', 'Created', 'Locked', 'Expired', 'Finished', 'Organization'];
</script>

<template>
  <!-- metrics -->
  <div class="ro-top">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-clipboard-text-clock-outline"></span>
        <div class="mc-total__title">Total Orders</div>
        <div class="mc-total__num">{{ totalOrders.toLocaleString() }}</div>
      </div>
      <div v-for="m in orderMetrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>

    <div class="ro-right">
      <div class="ro-card avgcard">
        <span class="avgcard__wm mdi mdi-timer-sand"></span>
        <div class="avgcard__title">Avg. Order Time</div>
        <div class="avgcard__num">{{ avgOrderTime.value }}<small>{{ avgOrderTime.unit }}</small></div>
        <div class="avgcard__foot">{{ avgOrderTime.foot }}</div>
      </div>
      <div class="ro-card">
        <div class="ro-card__head">
          <span class="mdi mdi-chart-bar"></span>
          <h3>Last Week Orders</h3>
          <span class="sub">{{ weekTotal }} total</span>
        </div>
        <div class="weekchart">
          <div v-for="d in weekDemo" :key="d.day" class="weekbar" :class="{ 'is-top': d.v === weekMax }">
            <div class="weekbar__val">{{ d.v }}</div>
            <div class="weekbar__track">
              <div class="weekbar__fill" :style="{ height: `${(d.v / weekMax) * 100}%` }"></div>
            </div>
            <div class="weekbar__day">{{ d.day }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- toolbar -->
  <div class="toolbar">
    <div class="field-search">
      <span class="mdi mdi-magnify"></span>
      <input v-model="searchOrderId" placeholder="Search by Order ID…" @input="onSearchInput">
    </div>
    <div class="field-select">
      <span class="mdi mdi-tag-outline lead"></span>
      <select v-model="status" @change="applyFilters">
        <option value="">All statuses</option>
        <option v-for="(meta, key) in STATUS_META" :key="key" :value="key">{{ meta.label }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="isAdmin && organizationList" class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="organization" @change="applyFilters">
        <option value="">All organizations</option>
        <option v-for="o in organizationList.results || []" :key="o.id" :value="o.id">{{ o.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="deviceList" class="field-select">
      <span class="mdi mdi-locker lead"></span>
      <select v-model="device" @change="applyFilters">
        <option value="">All devices</option>
        <option v-for="d in deviceList.results || []" :key="d.sn" :value="d.sn">{{ d.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ orderList?.count ?? 0 }} orders</span>
  </div>

  <!-- table -->
  <DataTable :cols="cols" :columns="columns" :rows="orders" empty-text="No orders match your filters.">
    <template #row="{ row }">
      <div class="td">
        <button class="linkcell" type="button" title="Open order" @click="goToOrder(row)">
          <span class="mdi mdi-clipboard-text-outline"></span>
          <span class="mono strong">#{{ row.id }}</span>
        </button>
      </div>
      <div class="td">
        <span>
          <span class="mdi mdi-locker" style="color: var(--fg-2); margin-right: 5px;"></span>{{ row.device || '—' }}
        </span>
      </div>
      <div class="td"><span class="mono">{{ row.cell_number ?? '—' }}</span></div>
      <div class="td">
        <span class="pill" :class="`pill--${(STATUS_META[row.status] || STATUS_META.created).pill}`">
          <span class="d"></span>
          {{ (STATUS_META[row.status] || { label: row.status }).label }}
        </span>
      </div>
      <div class="td"><span class="muted">{{ fmt(row.created_at) }}</span></div>
      <div class="td"><span class="muted">{{ fmt(row.locked_at) }}</span></div>
      <div class="td"><span class="muted">{{ fmt(row.expired_at) }}</span></div>
      <div class="td"><span class="muted">{{ fmt(row.finished_at) }}</span></div>
      <div class="td"><span>{{ row.organization || '—' }}</span></div>
      <RowActions
        :copies="[{ icon: 'content-copy', label: 'Copy Order ID', text: row.id }]"
        detail-label="Order"
        @detail="goToOrder(row)" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="orderList?.total_pages || 1"
    :count="orderList?.count || 0"
    @update:page="(p) => { page = p; getFilteredOrderList(); }" />
</template>
